import { apiService } from "../services/api";
import {
  configCache,
  screenConfigRegistry,
} from "../state/utils/configUtils";
import {
  ComponentConfig,
  ComponentType,
  FlexDirection,
  LayoutTypes,
} from "../services/types/ComponentTypes";

export type BillAllocationTab =
  | "customer"
  | "customer_ref_settlement"
  | "vendor"
  | "vendor_ref_settlement";

const BILL_ALLOCATION_TABS: ComponentConfig = {
  componentType: ComponentType.CUSTOM,
  customType: "billAllocationTabs",
} as ComponentConfig;

const SUMMARY_GROUP_STYLE = {
  backgroundColor: "#ffffff",
  direction: FlexDirection.COLUMN,
  borderRadius: 12,
  padding: 16,
};

const FAB_BY_TAB: Record<
  BillAllocationTab,
  {
    type: string;
    screenId?: string;
    action: string;
    tooltip?: string;
    partyType?: "customer" | "vendor";
    returnTab?: BillAllocationTab;
  }
> = {
  customer: {
    type: "add",
    screenId: "188",
    action: "navigate_modulev1/bill_allocations",
  },
  customer_ref_settlement: {
    type: "add",
    screenId: "199",
    action: "navigate_modulev1/reference_settlement",
    partyType: "customer",
    returnTab: "customer_ref_settlement",
    tooltip: "New reference settlement",
  },
  vendor: {
    type: "add",
    screenId: "193",
    action: "navigate_modulev1/vendor_bill_allocations",
  },
  vendor_ref_settlement: {
    type: "add",
    screenId: "198",
    action: "navigate_modulev1/vendor_reference_settlement",
    partyType: "vendor",
    returnTab: "vendor_ref_settlement",
    tooltip: "New vendor reference settlement",
  },
};

export function isReferenceSettlementTab(tab: BillAllocationTab): boolean {
  return tab === "customer_ref_settlement" || tab === "vendor_ref_settlement";
}

export function referenceSettlementPartyType(
  tab: BillAllocationTab
): "customer" | "vendor" {
  return tab === "vendor" || tab === "vendor_ref_settlement"
    ? "vendor"
    : "customer";
}

export function isSummaryListNode(node: any): boolean {
  const t = String(node?.componentType ?? "").toLowerCase();
  return t === "summerylist" || t === "summarylist" || t === "summary_list";
}

export function isReferenceSettlementPanelNode(node: any): boolean {
  return (
    String(node?.componentType ?? "").toLowerCase() === "custom" &&
    node?.customType === "referenceSettlementPanel"
  );
}

export function buildRefSettlementSummaryList(
  sourceConfig: ComponentConfig[] | null | undefined,
  partyType: "customer" | "vendor"
): ComponentConfig | null {
  const summary = extractSummaryListFromConfig(sourceConfig);
  if (!summary) return null;

  const cloned = JSON.parse(JSON.stringify(summary)) as any;
  if (cloned.Form) {
    cloned.Form.Query = `SELECT * FROM view_reference_settlement_list WHERE party_type = '${partyType}' ORDER BY settlement_date DESC, settlement_no DESC`;
    delete cloned.Form.View;
    cloned.refSettlementPartyType = partyType;
  }
  return cloned as ComponentConfig;
}

export function extractSummaryListFromConfig(
  components: ComponentConfig[] | null | undefined
): ComponentConfig | null {
  if (!components?.length) return null;
  let found: ComponentConfig | null = null;

  const walk = (nodes: ComponentConfig[]) => {
    for (const node of nodes) {
      if (found) return;
      if (isSummaryListNode(node)) {
        found = node;
        return;
      }
      const kids = (node as any).childComponents;
      if (Array.isArray(kids)) walk(kids);
    }
  };

  walk(components);
  return found;
}

export async function resolveScreenConfigForHub(
  screenId: string
): Promise<ComponentConfig[]> {
  const sid = String(screenId);
  const cached = configCache[sid]?.config;
  if (cached?.length) {
    return cached;
  }

  try {
    const response = await apiService.getScreenConfig(sid);
    if (response?.success && response?.data?.settings) {
      const apiSettings = response.data.settings as ComponentConfig | ComponentConfig[];
      const apiConfig = Array.isArray(apiSettings) ? apiSettings : [apiSettings];
      configCache[sid] = { config: apiConfig, source: "API" };
      return apiConfig;
    }
  } catch {
    // fall through to local registry
  }

  const fallback = screenConfigRegistry[sid];
  if (!fallback) return [];
  return Array.isArray(fallback) ? fallback : [fallback];
}

export function patchBillAllocationHubScreenConfig(
  components: ComponentConfig[],
  options: {
    activeTab: BillAllocationTab;
    customerConfig: ComponentConfig[] | null;
    vendorConfig: ComponentConfig[] | null;
    refSettlementConfig: ComponentConfig[] | null;
  }
): ComponentConfig[] {
  const { activeTab, customerConfig, vendorConfig, refSettlementConfig } =
    options;
  const root = components[0] as any;
  if (!root) return components;

  let contentChild: ComponentConfig;
  if (isReferenceSettlementTab(activeTab)) {
    const partyType = referenceSettlementPartyType(activeTab);
    const summaryList = buildRefSettlementSummaryList(
      refSettlementConfig,
      partyType
    );
    contentChild = summaryList ?? {
      componentType: LayoutTypes.GROUP,
      style: SUMMARY_GROUP_STYLE,
      childComponents: [],
    };
  } else {
    const sourceConfig =
      activeTab === "vendor" ? vendorConfig : customerConfig;
    const summaryList = extractSummaryListFromConfig(sourceConfig);
    contentChild = summaryList ?? {
      componentType: LayoutTypes.GROUP,
      style: SUMMARY_GROUP_STYLE,
      childComponents: [],
    };
  }

  const nextRoot: any = {
    ...root,
    title: "Bill wise allocations",
    fabAction: FAB_BY_TAB[activeTab],
    childComponents: [
      BILL_ALLOCATION_TABS,
      {
        componentType: LayoutTypes.GROUP,
        style: SUMMARY_GROUP_STYLE,
        childComponents: [contentChild],
      },
    ],
  };

  return [nextRoot as ComponentConfig];
}

export function normalizeBillAllocationTab(value: unknown): BillAllocationTab {
  const raw = String(value ?? "").toLowerCase();
  if (raw === "vendor") return "vendor";
  if (raw === "vendor_ref_settlement") return "vendor_ref_settlement";
  if (raw === "customer_ref_settlement") return "customer_ref_settlement";
  return "customer";
}
